import React, { useEffect, useState } from "react";
import { Send, Megaphone, Inbox, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const box = "w-full rsp-card px-4 py-3 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] resize-none";

// Composer for recruiters/admins to broadcast to not-yet-shipped recruits.
export function BroadcastComposer() {
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState([]);

  const loadAudience = () => api.get("/me/broadcast-audience").then((r) => setAudience(r.data.count)).catch(() => setAudience(null));
  const loadSent = () => api.get("/me/broadcasts").then((r) => setSent(r.data)).catch(() => {});
  useEffect(() => { loadAudience(); loadSent(); }, []);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/me/broadcast", { body });
      toast.success(`Message sent to ${data.recipient_count} recruit${data.recipient_count === 1 ? "" : "s"}.`);
      setBody("");
      loadSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message.");
    } finally { setSending(false); }
  };

  return (
    <div className="rsp-card p-4 space-y-3" data-testid="broadcast-composer">
      <div className="flex items-center justify-between">
        <div className="font-head uppercase tracking-wide flex items-center gap-2"><Megaphone size={18} className="gold" /> Message Recruits</div>
        {audience !== null && (
          <span className="text-xs eyebrow flex items-center gap-1 text-[var(--rsp-gray)]" style={{ letterSpacing: "0.1em" }} data-testid="broadcast-audience">
            <Users size={13} /> {audience} will receive
          </span>
        )}
      </div>
      <p className="text-[var(--rsp-muted)] text-xs">Goes only to recruits who have not shipped to Basic yet. Recruits at Basic or AIT will not receive it.</p>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Write a message to your pre-ship recruits…" className={box} data-testid="broadcast-input" />
      <button onClick={send} disabled={sending || !body.trim()} className="btn-gold w-full py-2.5 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="broadcast-send">
        <Send size={16} /> {sending ? "Sending…" : "Send Message"}
      </button>
      {sent.length > 0 && (
        <div className="pt-2 border-t border-[var(--rsp-border)]">
          <div className="eyebrow mb-2" style={{ letterSpacing: "0.14em" }}>Sent · {sent.length}</div>
          <div className="space-y-2 max-h-52 overflow-y-auto rsp-scroll">
            {sent.map((m) => (
              <div key={m.id} className="bg-[var(--rsp-bg-2)] rounded p-2.5 text-sm" data-testid={`sent-msg-${m.id}`}>
                <div className="text-[var(--rsp-cream)]">{m.body}</div>
                <div className="text-[var(--rsp-muted)] text-xs mt-1">{new Date(m.created_at).toLocaleString()} · {m.recipient_count} recipient{m.recipient_count === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Recruit inbox of received broadcast messages.
export function RecruitInbox() {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    api.get("/me/messages").then((r) => {
      setMsgs(r.data);
      if (r.data.length > 0) {
        api.post("/me/messages/read").then(() => window.dispatchEvent(new Event("messages-read"))).catch(() => {});
      }
    }).catch(() => {});
  }, []);
  if (msgs.length === 0) return null;
  return (
    <section data-testid="recruit-inbox">
      <div className="eyebrow mb-3 flex items-center gap-2" style={{ letterSpacing: "0.16em" }}><Inbox size={14} /> Messages</div>
      <div className="space-y-3">
        {msgs.map((m) => (
          <div key={m.id} className="rsp-card p-4 border-l-4" style={{ borderLeftColor: "var(--rsp-gold)" }} data-testid={`inbox-msg-${m.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <Megaphone size={14} className="gold" />
              <span className="font-head uppercase tracking-wide text-sm">{m.sender_name}</span>
              <span className="text-[var(--rsp-muted)] text-xs">· {m.sender_role}</span>
            </div>
            <p className="text-[var(--rsp-gray)] leading-relaxed pl-5">{m.body}</p>
            <div className="text-[var(--rsp-muted)] text-xs mt-1 pl-5">{new Date(m.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
